import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../hooks/useAuth'
import { callEdgeFunction } from '../lib/supabase'
import { ErrorAlert } from '../components/ui'
import { SALUTATIONS, PROFESSIONAL_ROLES } from '../lib/constants'

export default function FacilitatorSignup() {
  const { t, i18n } = useTranslation('auth')
  const { t: tCommon } = useTranslation('common')
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    salutation: '',
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organization_name: '',
    organization_address: '',
    professional_role: '',
    professional_role_other: '',
    privacy_consent: false,
  })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  if (user) {
    return <Navigate to="/" replace />
  }

  const updateField = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (formData.password.length < 6) {
      setError(t('facilitatorSignup.passwordMinLength'))
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('facilitatorSignup.passwordMismatch'))
      return
    }

    if (!formData.professional_role) {
      setError(t('facilitatorSignup.selectProfessionalRole'))
      return
    }

    if (formData.professional_role === 'Others' && !formData.professional_role_other.trim()) {
      setError(t('facilitatorSignup.specifyProfessionalRole'))
      return
    }

    if (!formData.privacy_consent) {
      setError(t('facilitatorSignup.privacyRequired'))
      return
    }

    setLoading(true)
    try {
      const { confirmPassword, privacy_consent, ...submitData } = formData
      await callEdgeFunction('register-facilitator', { ...submitData, privacy_accepted: true, preferred_language: i18n.language })
      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <div className="w-full max-w-md text-center animate-fadeIn">
          <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="font-display text-2xl font-medium text-gray-800 mb-3">
            {t('facilitatorSignup.successTitle')}
          </h1>
          <p className="text-gray-600 mb-2">
            {t('facilitatorSignup.successMessage')}
          </p>
          <p className="text-gray-500 text-sm mb-6">
            {t('facilitatorSignup.successDetail')}
          </p>
          <Link
            to="/login"
            className="text-primary-600 font-semibold hover:underline"
          >
            {t('facilitatorSignup.goToLogin')}
          </Link>
        </div>
      </div>
    )
  }

  const inputClass = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-lg animate-fadeIn">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-medium text-gray-800 mb-2">
            {t('facilitatorSignup.title')}
          </h1>
          <p className="text-gray-500">{t('facilitatorSignup.subtitle')}</p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm">
          <ErrorAlert message={error} onDismiss={() => setError(null)} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-[140px_1fr] gap-4">
              <div>
                <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.salutationLabel')}</label>
                <select
                  className={inputClass}
                  value={formData.salutation}
                  onChange={updateField('salutation')}
                >
                  <option value="">{t('facilitatorSignup.salutationPlaceholder')}</option>
                  {SALUTATIONS.map((s, idx) => {
                    const labels = i18n.t('salutations', { ns: 'common', returnObjects: true })
                    return <option key={s} value={s}>{Array.isArray(labels) ? labels[idx] || s : s}</option>
                  })}
                </select>
              </div>
              <div>
                <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.fullNameLabel')}</label>
                <input
                  type="text"
                  className={inputClass}
                  value={formData.full_name}
                  onChange={updateField('full_name')}
                  placeholder={t('facilitatorSignup.fullNamePlaceholder')}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.emailLabel')}</label>
              <input
                type="email"
                className={inputClass}
                value={formData.email}
                onChange={updateField('email')}
                placeholder={t('facilitatorSignup.emailPlaceholder')}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.passwordLabel')}</label>
                <input
                  type="password"
                  className={inputClass}
                  value={formData.password}
                  onChange={updateField('password')}
                  placeholder={t('facilitatorSignup.passwordPlaceholder')}
                  required
                />
              </div>
              <div>
                <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.confirmPasswordLabel')}</label>
                <input
                  type="password"
                  className={inputClass}
                  value={formData.confirmPassword}
                  onChange={updateField('confirmPassword')}
                  placeholder={t('facilitatorSignup.confirmPasswordPlaceholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.organisationNameLabel')}</label>
              <input
                type="text"
                className={inputClass}
                value={formData.organization_name}
                onChange={updateField('organization_name')}
                placeholder={t('facilitatorSignup.organisationNamePlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.organisationAddressLabel')}</label>
              <textarea
                className={inputClass + ' resize-none'}
                rows={2}
                value={formData.organization_address}
                onChange={updateField('organization_address')}
                placeholder={t('facilitatorSignup.organisationAddressPlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.professionalRoleLabel')}</label>
              <select
                className={inputClass}
                value={formData.professional_role}
                onChange={updateField('professional_role')}
                required
              >
                <option value="">{t('facilitatorSignup.professionalRolePlaceholder')}</option>
                {PROFESSIONAL_ROLES.map((role, idx) => {
                  const labels = i18n.t('professionalRoles', { ns: 'common', returnObjects: true })
                  return <option key={role} value={role}>{Array.isArray(labels) ? labels[idx] || role : role}</option>
                })}
              </select>
            </div>

            {formData.professional_role === 'Others' && (
              <div>
                <label className="block font-semibold text-gray-800 mb-2">{t('facilitatorSignup.specifyRoleLabel')}</label>
                <input
                  type="text"
                  className={inputClass}
                  value={formData.professional_role_other}
                  onChange={updateField('professional_role_other')}
                  placeholder={t('facilitatorSignup.specifyRolePlaceholder')}
                  required
                />
              </div>
            )}

            <div className="border-t border-gray-200 pt-5">
              <h2 className="font-display text-lg font-medium text-gray-800 mb-3">
                {tCommon('privacy.title')}
              </h2>
              <div className="text-gray-600 text-sm whitespace-pre-line leading-relaxed max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4">
                {tCommon('privacy.text')}
              </div>
              <label className="flex items-start gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 hover:bg-gray-50 transition-all">
                <input
                  type="checkbox"
                  checked={formData.privacy_consent}
                  onChange={(e) => setFormData(prev => ({ ...prev, privacy_consent: e.target.checked }))}
                  className="w-5 h-5 accent-primary-500 mt-0.5 shrink-0"
                />
                <span className="text-gray-700 text-sm font-medium">
                  {tCommon('privacy.consentLabel')}
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !formData.privacy_consent}
              className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? t('facilitatorSignup.registering') : t('facilitatorSignup.register')}
            </button>
          </form>

          <div className="text-center mt-5">
            <p className="text-gray-500 text-sm">
              {t('facilitatorSignup.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-primary-600 font-semibold hover:underline">
                {t('facilitatorSignup.signInLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
